import { useEffect, useState } from 'react';
import { Box, Heading, HStack, SimpleGrid, Spinner, Stack, Text } from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import {
  getDashboard,
  getCartera,
  type DashboardResponse,
  type CarteraContractRow,
} from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { useMe } from '@/lib/MeContext';
import { KpiCard } from '@/components/dashboard/KpiCard';
import { DateRangePicker, rangeFor, type Preset } from '@/components/dashboard/DateRangePicker';
import { MonthlyCollectionsChart } from '@/components/dashboard/MonthlyCollectionsChart';
import { AgingDonut } from '@/components/dashboard/AgingDonut';
import { AttentionList } from '@/components/dashboard/AttentionList';
import { fmtMoneyWhole } from '@/utils/format';

function readState(params: URLSearchParams): { preset: Preset; from: string; to: string } {
  const preset = (params.get('preset') as Preset) || 'thisMonth';
  let from = params.get('from') ?? '';
  let to = params.get('to') ?? '';
  if (!from || !to) {
    const r = rangeFor(preset);
    from = r.from;
    to = r.to;
  }
  return { preset, from, to };
}

function pctDelta(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

export function Dashboard() {
  const { activeProject } = useProject();
  const { me } = useMe();
  const [params, setParams] = useSearchParams();
  const { preset, from, to } = readState(params);
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [cartera, setCartera] = useState<CarteraContractRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Period-scoped: refetch the dashboard aggregates whenever the range changes.
  useEffect(() => {
    if (!activeProject) {
      setData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDashboard({ projectId: activeProject.id, from, to })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'No se pudo cargar el inicio');
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeProject?.id, from, to]);

  // Not period-scoped: the worklist shows current overdue contracts, so it only
  // needs to reload when the active project changes — not on every range tweak.
  useEffect(() => {
    if (!activeProject) {
      setCartera([]);
      return;
    }
    let cancelled = false;
    getCartera(activeProject.id, 'active')
      .then((c) => {
        if (!cancelled) setCartera(c.contracts);
      })
      .catch(() => {
        if (!cancelled) setCartera([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeProject?.id]);

  function updateRange(next: { preset: Preset; from: string; to: string }) {
    const p = new URLSearchParams(params);
    p.set('preset', next.preset);
    p.set('from', next.from);
    p.set('to', next.to);
    setParams(p, { replace: true });
  }

  if (!activeProject) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="10" textAlign="center" maxW="2xl" mx="auto">
          <Text color="fg.muted">
            Selecciona un proyecto en la barra superior para ver su información.
          </Text>
        </Box>
      </Box>
    );
  }

  const firstName = me ? me.fullName.split(' ')[0] : '';

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="6xl">
        <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'flex-end' }} gap="4">
          <Stack gap="1">
            <Text fontSize="xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.06em" fontWeight="700">
              {activeProject.code} · {activeProject.name}
            </Text>
            <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
              Hola{firstName ? `, ${firstName}` : ''}.
            </Heading>
            <Text color="fg.muted" fontSize={{ base: 'sm', md: 'md' }}>
              Resumen de cobranza y ventas del proyecto activo.
            </Text>
          </Stack>
          <DateRangePicker preset={preset} from={from} to={to} onChange={updateRange} />
        </Stack>

        {error && (
          <Text color="red.600" fontSize="sm">
            {error}
          </Text>
        )}

        {loading && !data ? (
          <HStack gap="3" color="fg.muted">
            <Spinner size="sm" />
            <Text fontSize="sm">Cargando…</Text>
          </HStack>
        ) : data ? (
          <Stack gap="6">
            <SimpleGrid columns={{ base: 2, lg: 4 }} gap="4">
              <KpiCard
                title="Cartera pendiente"
                value={fmtMoneyWhole(data.financiero.outstandingPortfolio)}
                caption="Saldo total por cobrar"
              />
              <KpiCard
                title="Recaudado del periodo"
                value={fmtMoneyWhole(data.financiero.periodCollections)}
                caption={`${from} → ${to}`}
                tone="positive"
                delta={{
                  pct: pctDelta(
                    Number(data.financiero.periodCollections),
                    Number(data.previous.periodCollections),
                  ),
                  label: 'vs. periodo anterior',
                }}
              />
              <KpiCard
                title="Saldo en mora"
                value={fmtMoneyWhole(data.financiero.moraOutstanding)}
                caption={
                  Number(data.financiero.outstandingPortfolio) > 0
                    ? `${Math.round(
                        (Number(data.financiero.moraOutstanding) /
                          Number(data.financiero.outstandingPortfolio)) *
                          100,
                      )}% de la cartera`
                    : 'Sin cartera vigente'
                }
                tone={Number(data.financiero.moraOutstanding) > 0 ? 'danger' : 'default'}
                linkTo="/contratos?status=active&hasOverdue=1"
              />
              <KpiCard
                title="Próximos 30 días"
                value={
                  <>
                    {data.financiero.upcoming30dCount.toLocaleString('es-GT')}
                    <Text as="span" fontSize="md" fontWeight="500" color="fg.muted" ml="2">
                      · {fmtMoneyWhole(data.financiero.upcoming30dAmount)}
                    </Text>
                  </>
                }
                caption="Cuotas + monto por vencer"
              />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 2 }} gap="4">
              <Stack gap="2">
                <Text fontSize="sm" fontWeight="600">
                  Recaudación por mes (últimos 6 meses)
                </Text>
                <MonthlyCollectionsChart data={data.trends.monthlyCollections} />
              </Stack>
              <AgingDonut aging={data.financiero.aging} />
            </SimpleGrid>

            <AttentionList rows={cartera} />

            <Stack gap="3">
              <Text fontSize="sm" fontWeight="700" color="fg.muted" textTransform="uppercase" letterSpacing="0.06em">
                Resumen comercial
              </Text>
              <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} gap="4">
                <KpiCard
                  compact
                  title="Contratos activos"
                  value={data.comercial.activeContractsCount.toLocaleString('es-GT')}
                  caption="Vigentes"
                  linkTo="/contratos?status=active"
                />
                <KpiCard
                  compact
                  title="Nuevos en el periodo"
                  value={data.comercial.newContractsCount.toLocaleString('es-GT')}
                  caption="Contratos creados"
                  delta={{
                    pct: pctDelta(data.comercial.newContractsCount, data.previous.newContractsCount),
                    label: 'vs. anterior',
                  }}
                />
                <KpiCard
                  compact
                  title="Ventas de contado"
                  value={data.comercial.contadoSalesCount.toLocaleString('es-GT')}
                  caption="Pago al contado"
                  delta={{
                    pct: pctDelta(data.comercial.contadoSalesCount, data.previous.contadoSalesCount),
                    label: 'vs. anterior',
                  }}
                />
                <KpiCard
                  compact
                  title="Capital financiado"
                  value={fmtMoneyWhole(data.comercial.totalFinanced)}
                  caption="Total activos"
                />
                <KpiCard
                  compact
                  title="Ticket promedio"
                  value={fmtMoneyWhole(data.comercial.averageTicket)}
                  caption="Por contrato"
                />
              </SimpleGrid>
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}
